import type { TaskStatus } from '../services/api/types.gen';

export const EDITABLE_STATUSES = new Set<TaskStatus>([
  'not_started',
  'saved',
  'returned_for_review',
  'submitted',
  'reopened',
]);

/**
 * Every status a `/sign` call can act on, whoever the actor is.
 *
 * Mirrors `SIGNABLE_STATUSES` in `backend/app/services/task_signing.py`, which
 * is the source of truth; a backend drift guard
 * (`test_signable_statuses_matches_frontend_statuses_ts`) fails if the two
 * stop matching. Which of the two signatures a given status produces — and so
 * whether the e-signature password is asked for — is `useTaskPermissions`,
 * mirroring `resolve_sign_plan`.
 */
export const SIGNABLE_STATUSES = new Set<TaskStatus>([
  'not_started',
  'saved',
  'returned_for_review',
  'submitted',
  'pending_clinician_signature',
  'reopened',
]);

/**
 * Statuses that mean a task is finished. Used by the CMS-485 OASIS picker to
 * decide whether a source visit's data is safe to load — anything earlier
 * should be visually discouraged.
 */
export const COMPLETED_STATUSES = new Set<TaskStatus>([
  'completed',
  'exported',
]);

/**
 * Order statuses where the backend freezes goal writes on that order
 * (`verify_task_goal_editable` → `is_locked_order_physician_status`).
 *
 * Mirrors `LOCKED_ORDER_PHYSICIAN_STATUSES` in `backend/app/crud/order.py`,
 * which is the source of truth. Today it happens to be exactly the
 * complement of `EDITABLE_STATUSES` over `TaskStatus` (5 editable + 7 locked
 * = all 12 values, no overlap), but nothing pins that relationship — the two
 * sets are defined and can evolve independently, so do not assume a status
 * missing from one is automatically in the other. Used only to hide goal
 * write controls on an order's Goals card before they would 400; the
 * backend enforces this regardless of what the UI shows.
 */
export const LOCKED_ORDER_PHYSICIAN_STATUSES = new Set<TaskStatus>([
  'submitted_with_signature',
  'pending_clinician_signature',
  'completed',
  'exported',
  'sent_to_physician',
  'returned_with_signature',
  'sent_no_signature_required',
]);
